import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { format } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, MessageCirclePlus, Users, Lock } from "lucide-react";

interface Row {
  id: string;
  question_text: string;
  status: "open" | "pending" | "answered" | "closed";
  route: "gathering" | "private";
  answer_text: string | null;
  answered_at: string | null;
  notified_at: string | null;
  created_at: string;
}

const STATUS_LABEL: Record<Row["status"], string> = {
  open: "Waiting for the congregation",
  pending: "Coming soon",
  answered: "Answered",
  closed: "Not taken up",
};

export default function MyQuestion() {
  const { id } = useParams<{ id: string }>();
  const [row, setRow] = useState<Row | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "missing">("loading");

  useEffect(() => {
    if (!id) return;
    (async () => {
      const { data, error } = await supabase.rpc("get_my_gathering_question", { _id: id });
      if (error || !data || (data as any[]).length === 0) {
        setState("missing");
        return;
      }
      setRow((data as Row[])[0]);
      setState("ready");
    })();
  }, [id]);

  if (state === "loading") {
    return <div className="container max-w-2xl mx-auto px-4 py-10 text-sm text-muted-foreground">Loading...</div>;
  }
  if (state === "missing" || !row) {
    return (
      <div className="container max-w-2xl mx-auto px-4 py-10 space-y-4">
        <p className="text-sm text-muted-foreground">This question isn't available.</p>
        <Button asChild variant="outline"><Link to="/support/questions"><ArrowLeft className="w-4 h-4 mr-2" />Back to your questions</Link></Button>
      </div>
    );
  }

  return (
    <div className="container max-w-2xl mx-auto px-4 py-8 space-y-4">
      <Button asChild variant="ghost" size="sm" className="-ml-2">
        <Link to="/support/questions"><ArrowLeft className="w-4 h-4 mr-2" />Your questions</Link>
      </Button>

      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <CardTitle className="flex items-center gap-2 text-charcoal">
              <MessageCirclePlus className="w-5 h-5 text-gold" />
              Your question
            </CardTitle>
            <div className="flex items-center gap-1.5">
              <Badge variant="outline" className="text-[10px]">
                {row.route === "private" ? (
                  <><Lock className="w-3 h-3 mr-1" />Private</>
                ) : (
                  <><Users className="w-3 h-3 mr-1" />For the congregation</>
                )}
              </Badge>
              <Badge variant={row.status === "answered" ? "default" : row.status === "closed" ? "outline" : "secondary"}>
                {STATUS_LABEL[row.status]}
              </Badge>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Asked {format(new Date(row.created_at), "PP")}
          </p>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="rounded-md border bg-card p-4 whitespace-pre-wrap text-sm">
            {row.question_text}
          </div>

          {row.status === "answered" && row.answer_text ? (
            <div>
              <h2 className="text-sm font-semibold mb-2">Answer</h2>
              <div className="rounded-md bg-muted/60 p-4 whitespace-pre-wrap text-base leading-relaxed">
                {row.answer_text}
              </div>
              {row.answered_at && (
                <p className="text-xs text-muted-foreground mt-2">
                  Answered {format(new Date(row.answered_at), "PP")}
                </p>
              )}
            </div>
          ) : row.status === "closed" ? (
            <div className="rounded-md border p-4 text-sm text-muted-foreground">
              {row.answer_text || "This one wasn't taken up on the call."}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              We'll bring this to the weekly congregation. When there's an answer, it will appear here and we'll email you.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
